<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Bandeira de Pagamento' : 'Nova Bandeira de Pagamento' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarBandeira" @cancel="voltar">
      <FormSection title="Informações da Bandeira">
        <!-- Linha com os selects para Operador, Meio e Forma -->
        <div class="d-flex gap-3 mb-3">
          <!-- Campo Operador -->
          <div class="flex-fill larger-field">
            <SelectField
              id="operador_id"
              v-model="bandeira.operador_id"
              :options="operadoresOptions"
              placeholder="Selecione o operador"
              label="Operador <span class='required'>*</span>"
              required
              @update:modelValue="onOperadorChange"
            />
            <div v-if="errors.operador_id" class="error-message">{{ errors.operador_id }}</div>
          </div>
          <!-- Campo Meio -->
          <div class="flex-fill larger-field">
            <SelectField
              id="meio_id"
              v-model="bandeira.meio_id"
              :options="meiosOptions"
              placeholder="Selecione o meio"
              label="Meio <span class='required'>*</span>"
              required
              @update:modelValue="onMeioChange"
            />
            <div v-if="errors.meio_id" class="error-message">{{ errors.meio_id }}</div>
          </div>
          <!-- Campo Forma -->
          <div class="flex-fill larger-field">
            <SelectField
              id="forma_id"
              v-model="bandeira.forma_id"
              :options="formasOptions"
              placeholder="Selecione a forma"
              label="Forma <span class='required'>*</span>"
              required
            />
            <div v-if="errors.forma_id" class="error-message">{{ errors.forma_id }}</div>
          </div>
          <!-- Campo Bandeira -->
          <div class="mb-3">
            <InputField
              id="bandeira"
              v-model="bandeira.bandeira"
              placeholder="Digite a bandeira de pagamento"
              label="Bandeira de Pagamento <span class='required'>*</span>"
              required
              :class="{ 'error-input': errors.bandeira }"
            />
            <div v-if="errors.bandeira" class="error-message">{{ errors.bandeira }}</div>
          </div>
        </div>
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";
import axios from "@/services/axios";

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // Objeto reativo para a bandeira (campo "ativo" removido)
    const bandeira = reactive({
      id: null,
      operador_id: '',
      meio_id: '',
      forma_id: '',
      bandeira: "",
    });
    const errors = reactive({});
    const isSubmitting = ref(false);

    // Operadores
    const operadores = ref([]);
    const operadoresOptions = computed(() =>
      operadores.value.map(op => ({ text: op.operador, value: op.id }))
    );

    // Meios (serão carregados conforme o operador selecionado)
    const meios = ref([]);
    const meiosOptions = computed(() =>
      meios.value.map(m => ({ text: m.meio, value: m.id }))
    );

    // Formas (serão carregadas conforme o meio selecionado e operador)
    const formas = ref([]);
    const formasOptions = computed(() =>
      formas.value.map(f => ({ text: f.forma, value: f.id }))
    );

    // Função para buscar operadores
    const fetchOperadores = async () => {
      try {
        const response = await axios.get('/financeiro/parametros/operadores');
        operadores.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar operadores:", error);
      }
    };

    // Função para buscar meios filtrados pelo operador selecionado
    const fetchMeios = async (operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/meios', {
          params: { operador_id: operadorId }
        });
        meios.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar meios:", error);
      }
    };

    // Função para buscar formas filtradas pelo meio e operador selecionados
    const fetchFormas = async (meioId, operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/formas', {
          params: { meio_id: meioId, operador_id: operadorId }
        });
        formas.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar formas:", error);
      }
    };

    // Quando o operador muda: limpar os campos de meio e forma e buscar os meios relacionados
    const onOperadorChange = (newOperadorId) => {
      bandeira.meio_id = '';
      bandeira.forma_id = '';
      formas.value = [];
      if (newOperadorId) {
        fetchMeios(newOperadorId);
      } else {
        meios.value = [];
      }
    };

    // Quando o meio muda: limpar o campo forma e buscar as formas filtradas por meio e operador
    const onMeioChange = (newMeioId) => {
      bandeira.forma_id = '';
      if (newMeioId && bandeira.operador_id) {
        fetchFormas(newMeioId, bandeira.operador_id);
      } else {
        formas.value = [];
      }
    };

    // Validação do formulário
    const validateForm = () => {
      errors.operador_id = bandeira.operador_id ? "" : "Operador é obrigatório.";
      errors.meio_id = bandeira.meio_id ? "" : "Meio é obrigatório.";
      errors.forma_id = bandeira.forma_id ? "" : "Forma é obrigatória.";
      errors.bandeira = bandeira.bandeira ? "" : "A bandeira de pagamento é obrigatória.";
    };

    const isFormValid = computed(() => {
      return bandeira.operador_id && bandeira.meio_id && bandeira.forma_id && bandeira.bandeira.trim() !== "" &&
             !Object.values(errors).some(error => error);
    });

    const novoBandeira = () => {
      Object.assign(bandeira, {
        operador_id: '',
        meio_id: '',
        forma_id: '',
        bandeira: "",
      });
      for (const key in errors) {
        if (Object.hasOwn(errors, key)) errors[key] = "";
      }
    };

    const salvarBandeira = async () => {
      if (isSubmitting.value) return;
      validateForm();
      if (!isFormValid.value) {
        console.error("O formulário contém erros:", errors);
        return;
      }
      isSubmitting.value = true;
      try {
        const method = bandeira.id ? "put" : "post";
        const url = bandeira.id
          ? `/financeiro/parametros/bandeiras/${bandeira.id}`
          : "/financeiro/parametros/bandeiras";
        await axios[method](url, {
          operador_id: bandeira.operador_id,
          meio_id: bandeira.meio_id,
          forma_id: bandeira.forma_id,
          bandeira: bandeira.bandeira,
        });
        router.push("/financeiro/parametros/bandeiras");
      } catch (error) {
        console.error("Erro ao salvar bandeira:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/financeiro/parametros/bandeiras");
    };

    onMounted(async () => {
      await fetchOperadores();
      const bandeiraId = route.params.id;
      if (bandeiraId) {
        try {
          const response = await axios.get(`/financeiro/parametros/bandeiras/${bandeiraId}`);
          Object.assign(bandeira, response.data);
          // Após carregar a bandeira, carregar os meios e formas encadeados
          if (bandeira.operador_id) {
            await fetchMeios(bandeira.operador_id);
          }
          if (bandeira.meio_id && bandeira.operador_id) {
            await fetchFormas(bandeira.meio_id, bandeira.operador_id);
          }
        } catch (error) {
          console.error("Erro ao carregar bandeira:", error);
        }
      } else {
        novoBandeira();
      }
    });

    return {
      bandeira,
      errors,
      isFormValid,
      isSubmitting,
      salvarBandeira,
      voltar,
      route,
      operadoresOptions,
      meiosOptions,
      formasOptions,
      onOperadorChange,
      onMeioChange,
    };
  },
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.error-input {
  border-color: #dc3545;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
.form-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* Classe para aumentar a largura mínima dos campos */
.larger-field {
  min-width: 180px; /* Ajuste esse valor conforme necessário */
}
</style>
